import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { del as apiDelete } from 'data/services/apiService';
import { getApiUrl } from 'data/services/apiConstants';

import StandardModal from 'modals/StandardModal';
import Spinner from 'components/Spinner';
import Alert from 'view/components/Alert';
import { closeModal, openModal } from 'data/reducers/modalReducer';
import { MODAL_TYPES } from 'view/ModalManager';
import { showToast } from 'data/reducers/toastReducer';
import { performDowngradeAfterLimitsCheck } from 'data/reducers/subscriptionReducer';

const SubscriptionExceedLimitModal = ({ options }) => {
	const dispatch = useDispatch();
	const { limitsData, actionType, targetLimits, targetPlanInfo } =
		options || {};

	const [selectedItems, setSelectedItems] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const [removingItemId, setRemovingItemId] = useState(null);
	const [currentStep, setCurrentStep] = useState('overview'); // 'overview', 'properties', 'users'

	const { authToken } = useSelector((state) => ({
		authToken: state.user.authToken,
	}));

	// Use target limits from props, fallback to free plan limits for backward compatibility
	const planLimits = targetLimits || { properties: 30, seats: 3 };
	const currentActionType = actionType || 'cancel';

	const { usage } = limitsData;
	const exceedsPropertyLimit = usage.properties > planLimits.properties;
	const exceedsSeatsLimit = usage.seats > planLimits.seats;

	let action = `${currentActionType}_manage_properties`;

	if (!exceedsPropertyLimit && exceedsSeatsLimit) {
		action = `${currentActionType}_manage_users`;
	} else if (!exceedsPropertyLimit && !exceedsSeatsLimit) {
		action = null; // No limits exceeded
	}

	const getModalConfig = () => {
		let actionText = 'Cancellation';
		if (currentActionType === 'cancel') {
			actionText = 'Cancellation';
		} else if (currentActionType === 'downgrade') {
			actionText = 'Downgrade';
		} else if (currentActionType === 'usage') {
			actionText = 'Usage';
		}
		const planText = targetPlanInfo?.name ? ` to ${targetPlanInfo.name}` : '';

		switch (action) {
			case `${currentActionType}_manage_properties`:
				return {
					title: `Reduce Properties to Continue ${actionText}${planText}`,
					type: 'properties',
					currentCount: limitsData?.properties?.length || 0,
					maxAllowed: planLimits.properties,
					itemsToRemove: Math.max(
						0,
						(limitsData?.properties?.length || 0) - planLimits.properties
					),
				};
			case `${currentActionType}_manage_users`:
				return {
					title: `Reduce Users to Continue ${actionText}${planText}`,
					type: 'users',
					currentCount: limitsData?.users?.length || 0,
					maxAllowed: planLimits.seats,
					itemsToRemove: Math.max(
						0,
						(limitsData?.users?.length || 0) - planLimits.seats
					),
				};
			default:
				return {
					title: `Reduce Usage to Continue ${actionText}`,
					type: 'unknown',
					currentCount: 0,
					maxAllowed: 0,
					itemsToRemove: 0,
				};
		}
	};

	const config = getModalConfig();

	const handleRemoveItem = async (itemId, itemType) => {
		setRemovingItemId(itemId);
		setError(null);
		setIsLoading(true);

		try {
			const endpoint =
				itemType === 'properties'
					? `${getApiUrl()}/api/portfolio-sets/property/${itemId}`
					: `${getApiUrl()}/api/portfolio-sets/user/${itemId}`;

			await apiDelete({
				url: endpoint,
				authToken,
				dispatch,
				onSuccess: () => {
					// Add to selected items list to track removal
					setSelectedItems((prev) => [...prev, itemId]);

					setIsLoading(false);

					// Show success toast
					dispatch(
						showToast({
							type: 'success',
							title: 'Success',
							message: `${
								itemType === 'properties' ? 'Property' : 'User'
							} removed successfully`,
							duration: 3000,
						})
					);

					// Check if we've reached the required limit
					checkLimitCompliance();

					if (currentActionType === 'cancel') {
						// dispatch(checkCancelLimits());
					}
				},
			});
		} catch (error) {
			setError(
				`Failed to remove ${
					itemType === 'properties' ? 'property' : 'user'
				}. Please try again.`
			);
			console.error('Error removing item:', error);
		} finally {
			setRemovingItemId(null);
		}
	};

	const checkLimitCompliance = () => {
		const currentCount = getCurrentItemCount();
		const removedCount = selectedItems.length + 1; // +1 for the item we just removed
		const remainingCount = currentCount - removedCount;

		if (remainingCount <= config.maxAllowed) {
			// Delay to allow UI to update before proceeding
			setTimeout(() => {
				handleQuotaReached();
			}, 500);
		}
	};

	const getCurrentItemCount = () => {
		if (config.type === 'properties') {
			return limitsData?.properties?.length || 0;
		} else {
			return limitsData?.users?.length || 0;
		}
	};

	const handleQuotaReached = () => {
		if (action === `${currentActionType}_manage_properties`) {
			// Check if user also exceeds seat limits
			const usersCount = limitsData?.users?.length || 0;

			if (usersCount > planLimits.seats) {
				// Open user management modal
				dispatch(
					openModal(MODAL_TYPES.SUBSCRIPTION_EXCEED_LIMIT, {
						action: `${currentActionType}_manage_users`,
						limitsData,
						targetLimits: planLimits,
						actionType: currentActionType,
						targetPlanInfo,
					})
				);
			} else {
				// Proceed to next action
				if (currentActionType === 'cancel') {
					// For cancel, proceed to cancellation reason
					dispatch(
						openModal(MODAL_TYPES.SUBSCRIPTION_ACTION, {
							action: 'cancel',
							limitsData,
						})
					);
				} else {
					// For downgrade, show confirm modal then perform downgrade
					dispatch(performDowngradeAfterLimitsCheck(targetPlanInfo));
				}
			}
		} else if (action === `${currentActionType}_manage_users`) {
			// After removing users, proceed to next action
			if (currentActionType === 'cancel') {
				// For cancel, proceed to cancellation reason
				dispatch(
					openModal(MODAL_TYPES.SUBSCRIPTION_ACTION, {
						action: 'cancel',
						limitsData,
					})
				);
			} else {
				// For downgrade, show confirm modal then perform downgrade
				dispatch(performDowngradeAfterLimitsCheck(targetPlanInfo));
			}
		}
	};

	const handleProceedToReason = () => {
		dispatch(
			openModal(MODAL_TYPES.SUBSCRIPTION_ACTION, {
				action: 'cancel',
				limitsData,
			})
		);
	};

	const renderPropertyTable = () => {
		const properties = limitsData?.properties || [];
		const propertyRemovedCount = selectedItems.filter((id) =>
			properties.some((p) => p.id === id)
		).length;
		const remainingCount = properties.length - propertyRemovedCount;
		const propertiesNeeded = Math.max(
			0,
			usage.properties - planLimits.properties
		);

		return (
			<div>
				<div className="mb-3">
					<p className="text-muted">
						You currently have <strong>{usage.properties} properties</strong>{' '}
						but the plan limit is{' '}
						<strong>{planLimits.properties} properties</strong>. Please remove{' '}
						<strong>{propertiesNeeded} properties</strong> to continue.
					</p>

					{/* Progress Bar */}
					<div className="progress mb-3" style={{ height: '10px' }}>
						<div
							className={`progress-bar ${
								propertyRemovedCount >= propertiesNeeded
									? 'bg-success'
									: 'bg-info'
							}`}
							style={{
								width: `${Math.min(
									100,
									(propertyRemovedCount / propertiesNeeded) * 100
								)}%`,
							}}
						>
							{propertyRemovedCount} / {propertiesNeeded}
						</div>
					</div>
				</div>

				<div className="mb-3">
					<div className="d-flex justify-content-between">
						<p className="small text-info mb-0">
							Removed: <strong>{propertyRemovedCount}</strong> / Need to remove:{' '}
							<strong>{propertiesNeeded}</strong>
						</p>
						<p className="small text-muted mb-0">
							Remaining: <strong>{remainingCount} properties</strong>
						</p>
					</div>
					{propertyRemovedCount >= propertiesNeeded && (
						<div className="alert alert-success py-2">
							<small>
								<i className="bi bi-check-circle me-1"></i>
								Property limit requirements met! You can proceed to the next
								step.
							</small>
						</div>
					)}
				</div>

				<div
					className="table-responsive"
					style={{ maxHeight: '400px', overflowY: 'auto' }}
				>
					<table className="table table-sm table-hover">
						<thead className="table-light sticky-top">
							<tr>
								<th>Address</th>
								<th>Portfolios</th>
								<th width="100">Action</th>
							</tr>
						</thead>
						<tbody>
							{properties.map((property) => {
								const isRemoved = selectedItems.includes(property.id);
								const portfolioNames =
									property.portfolioSets?.map((p) => p.name).join(', ') ||
									'No portfolios';

								return (
									<tr
										key={property.id}
										className={isRemoved ? 'table-danger text-muted' : ''}
									>
										<td>
											{isRemoved && <s>{property.address}</s>}
											{!isRemoved && property.address}
										</td>
										<td>{portfolioNames}</td>
										<td>
											{isRemoved ? (
												<span className="badge bg-danger">Removed</span>
											) : (
												<button
													className="btn btn-sm btn-outline-danger"
													onClick={() =>
														handleRemoveItem(property.id, 'properties')
													}
													disabled={removingItemId === property.id}
												>
													{removingItemId === property.id ? (
														<>
															<span className="spinner-border spinner-border-sm me-1" />
															Removing
														</>
													) : (
														<i className="bi bi-trash" />
													)}
												</button>
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</div>
		);
	};

	const renderUserTable = () => {
		const users = limitsData?.users || [];
		const userRemovedCount = selectedItems.filter((id) =>
			users.some((u) => u.id === id)
		).length;
		const remainingCount = users.length - userRemovedCount;
		const usersNeeded = Math.max(0, usage.seats - planLimits.seats);

		return (
			<div>
				<div className="mb-3">
					<p className="text-muted">
						You currently have <strong>{usage.seats} users</strong> but the plan
						limit is <strong>{planLimits.seats} users</strong>. Please remove{' '}
						<strong>{usersNeeded} users</strong> to continue.
					</p>

					{/* Progress Bar */}
					<div className="progress mb-3" style={{ height: '10px' }}>
						<div
							className={`progress-bar ${
								userRemovedCount >= usersNeeded ? 'bg-success' : 'bg-warning'
							}`}
							style={{
								width: `${Math.min(
									100,
									(userRemovedCount / usersNeeded) * 100
								)}%`,
							}}
						>
							{userRemovedCount} / {usersNeeded}
						</div>
					</div>
				</div>

				<div className="mb-3">
					<div className="d-flex justify-content-between">
						<p className="small text-info mb-0">
							Removed: <strong>{userRemovedCount}</strong> / Need to remove:{' '}
							<strong>{usersNeeded}</strong>
						</p>
						<p className="small text-muted mb-0">
							Remaining: <strong>{remainingCount} users</strong>
						</p>
					</div>
					{userRemovedCount >= usersNeeded && (
						<div className="alert alert-success py-2">
							<small>
								<i className="bi bi-check-circle me-1"></i>
								User limit requirements met! You can proceed to the next step.
							</small>
						</div>
					)}
				</div>

				<div
					className="table-responsive"
					style={{ maxHeight: '400px', overflowY: 'auto' }}
				>
					<table className="table table-sm table-hover">
						<thead className="table-light sticky-top">
							<tr>
								<th>Name</th>
								<th>Email</th>
								<th>Portfolios</th>
								<th width="100">Action</th>
							</tr>
						</thead>
						<tbody>
							{users.map((user) => {
								const isRemoved = selectedItems.includes(user.id);
								const portfolioNames =
									user.portfolioSets?.map((p) => p.name).join(', ') ||
									'No portfolios';

								return (
									<tr
										key={user.id}
										className={`${isRemoved ? 'table-danger text-muted' : ''} ${
											user.isOwner ? 'table-warning' : ''
										}`}
									>
										<td>
											{isRemoved && <s>{user.name}</s>}
											{!isRemoved && user.name}
											{user.isOwner && (
												<span className="badge bg-primary ms-2">Owner</span>
											)}
										</td>
										<td>
											{isRemoved && <s>{user.email}</s>}
											{!isRemoved && user.email}
										</td>
										<td>{portfolioNames}</td>
										<td>
											{user.isOwner ? (
												<span className="badge bg-warning text-dark">
													Owner
												</span>
											) : isRemoved ? (
												<span className="badge bg-danger">Removed</span>
											) : (
												<button
													className="btn btn-sm btn-outline-danger"
													onClick={() => handleRemoveItem(user.id, 'users')}
													disabled={removingItemId === user.id}
												>
													{removingItemId === user.id ? (
														<>
															<span className="spinner-border spinner-border-sm me-1" />
															Removing
														</>
													) : (
														<i className="bi bi-trash" />
													)}
												</button>
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</div>
		);
	};

	const renderOverview = () => {
		let actionText = `Cancellation`;
		if (currentActionType === 'cancel') {
			actionText = 'Cancellation';
		} else if (currentActionType === 'downgrade') {
			actionText = 'Downgrade';
		} else if (currentActionType === 'usage') {
			actionText = 'Usage';
		}
		const planText = targetPlanInfo?.name ? ` to ${targetPlanInfo.name}` : '';

		return (
			<div>
				<div className="mb-4">
					<h5>Usage Limits Overview</h5>
					<p className="text-muted">
						To proceed with {actionText.toLowerCase()}
						{planText}, you need to reduce your current usage to meet the plan
						limits.
					</p>
				</div>

				<div className="row">
					{/* Properties Card */}
					<div className="col-md-6">
						<div
							className={`card h-100 ${
								exceedsPropertyLimit ? 'border-danger' : 'border-success'
							}`}
						>
							<div className="card-header">
								<h6 className="mb-0">
									<i className="bi bi-buildings me-2"></i>
									Properties
									{exceedsPropertyLimit && (
										<span className="badge bg-danger ms-2">
											Action Required
										</span>
									)}
								</h6>
							</div>
							<div className="card-body">
								<div className="d-flex justify-content-between align-items-center mb-2">
									<span>Current:</span>
									<strong>{usage.properties}</strong>
								</div>
								<div className="d-flex justify-content-between align-items-center mb-3">
									<span>Plan Limit:</span>
									<strong>{planLimits.properties}</strong>
								</div>
								{exceedsPropertyLimit && (
									<div className="text-danger">
										<small>
											<i className="bi bi-exclamation-triangle me-1"></i>
											Need to remove {usage.properties -
												planLimits.properties}{' '}
											properties
										</small>
									</div>
								)}
								{!exceedsPropertyLimit && (
									<div className="text-success">
										<small>
											<i className="bi bi-check-circle me-1"></i>
											Within plan limits
										</small>
									</div>
								)}
							</div>
						</div>
					</div>

					{/* Users/Seats Card */}
					<div className="col-md-6">
						<div
							className={`card h-100 ${
								exceedsSeatsLimit ? 'border-danger' : 'border-success'
							}`}
						>
							<div className="card-header">
								<h6 className="mb-0">
									<i className="bi bi-people me-2"></i>
									Users
									{exceedsSeatsLimit && (
										<span className="badge bg-danger ms-2">
											Action Required
										</span>
									)}
								</h6>
							</div>
							<div className="card-body">
								<div className="d-flex justify-content-between align-items-center mb-2">
									<span>Current:</span>
									<strong>{usage.seats}</strong>
								</div>
								<div className="d-flex justify-content-between align-items-center mb-3">
									<span>Plan Limit:</span>
									<strong>{planLimits.seats}</strong>
								</div>
								{exceedsSeatsLimit && (
									<div className="text-danger">
										<small>
											<i className="bi bi-exclamation-triangle me-1"></i>
											Need to remove {usage.seats - planLimits.seats} users
										</small>
									</div>
								)}
								{!exceedsSeatsLimit && (
									<div className="text-success">
										<small>
											<i className="bi bi-check-circle me-1"></i>
											Within plan limits
										</small>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	};

	const renderContent = () => {
		switch (currentStep) {
			case 'overview':
				return renderOverview();
			case 'properties':
				return renderPropertyTable();
			case 'users':
				return renderUserTable();
			default:
				return renderOverview();
		}
	};

	const getModalTitle = () => {
		const actionText =
			currentActionType === 'cancel' ? 'Cancellation' : 'Downgrade';
		const planText = targetPlanInfo?.name ? ` to ${targetPlanInfo.name}` : '';

		switch (currentStep) {
			case 'properties':
				return `Manage Properties for ${actionText}${planText}`;
			case 'users':
				return `Manage Users for ${actionText}${planText}`;
			default:
				return `Usage Limits for ${actionText}${planText}`;
		}
	};

	const getFooterButtons = () => {
		const actionText =
			currentActionType === 'cancel' ? 'Cancellation' : 'Downgrade';

		switch (currentStep) {
			case 'overview':
				return [
					{
						label: 'Close',
						color: 'secondary',
						onClick: () => dispatch(closeModal()),
						disabled: isLoading,
					},
					...(exceedsPropertyLimit
						? [
								{
									label: 'Proceed for Properties',
									color: 'danger',
									onClick: () => setCurrentStep('properties'),
									disabled: isLoading,
								},
						  ]
						: []),
					...(exceedsSeatsLimit
						? [
								{
									label: 'Proceed for Users',
									color: 'warning',
									onClick: () => setCurrentStep('users'),
									disabled: isLoading,
								},
						  ]
						: []),
					...(!exceedsPropertyLimit && !exceedsSeatsLimit
						? [
								{
									label: `Proceed to ${actionText}`,
									color: 'primary',
									onClick: handleProceedToReason,
									disabled: isLoading,
								},
						  ]
						: []),
				];

			case 'properties':
			case 'users':
				const withinLimits =
					currentStep === 'properties'
						? selectedItems.filter((id) =>
								limitsData?.properties?.some((p) => p.id === id)
						  ).length >= Math.max(0, usage.properties - planLimits.properties)
						: selectedItems.filter((id) =>
								limitsData?.users?.some((u) => u.id === id)
						  ).length >= Math.max(0, usage.seats - planLimits.seats);

				return [
					{
						label: 'Back',
						color: 'secondary',
						onClick: () => setCurrentStep('overview'),
						disabled: isLoading || removingItemId !== null,
					},
					...(withinLimits
						? [
								{
									label: checkBothStepsComplete()
										? `Proceed to ${actionText}`
										: 'Next Step',
									color: 'primary',
									onClick: handleStepComplete,
									disabled: isLoading,
								},
						  ]
						: []),
				];

			default:
				return [];
		}
	};

	const checkBothStepsComplete = () => {
		const propertiesWithinLimit =
			usage.properties -
				selectedItems.filter((id) =>
					limitsData?.properties?.some((p) => p.id === id)
				).length <=
			planLimits.properties;

		const usersWithinLimit =
			usage.seats -
				selectedItems.filter((id) =>
					limitsData?.users?.some((u) => u.id === id)
				).length <=
			planLimits.seats;

		return (
			(!exceedsPropertyLimit || propertiesWithinLimit) &&
			(!exceedsSeatsLimit || usersWithinLimit)
		);
	};

	const handleStepComplete = () => {
		if (currentStep === 'properties' && exceedsSeatsLimit) {
			// Still need to handle users
			setCurrentStep('users');
		} else if (currentStep === 'users' && exceedsPropertyLimit) {
			// Still need to handle properties
			setCurrentStep('properties');
		} else {
			// All steps complete, proceed to final action
			handleProceedToReason();
		}
	};

	return (
		<StandardModal
			title={getModalTitle()}
			size="xl"
			closeOnOutsideClick={false}
			noFooter
		>
			<div>
				{error && (
					<Alert color="danger">
						<div>{error}</div>
					</Alert>
				)}

				{isLoading ? (
					<div className="text-center py-4">
						<Spinner />
						<p className="mt-2">Processing...</p>
					</div>
				) : (
					renderContent()
				)}

				{/* Custom Footer */}
				<div className="modal-footer">
					<div className="d-flex justify-content-end gap-2">
						{getFooterButtons().map((button, index) => (
							<button
								key={index}
								type="button"
								className={`btn btn-${button.color}`}
								onClick={button.onClick}
								disabled={button.disabled}
							>
								{button.label}
							</button>
						))}
					</div>
				</div>
			</div>
		</StandardModal>
	);
};

export default SubscriptionExceedLimitModal;
